import VendorBusinessWorkspace from './VendorBusinessWorkspace';
import VendorReleaseControls from './VendorReleaseControls';
import OrderGcodeUpload from './OrderGcodeUpload';
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import {
  Bell,
  Monitor,
  Download,
  Home,
  ClipboardList,
  Factory,
  Truck,
  Wrench,
  CreditCard,
  BookOpen,
  LogOut,
  RefreshCw,
  Clock,
  Check,
  AlertCircle,
  BedDouble,
  PaintRoller,
  Layers,
  MapPin,
  FileCheck,
  CalendarDays,
  ChevronRight,
  X,
} from "lucide-react";
import {
  DEMO_VENDOR_ORDER,
  vendorActionPatch,
  type VendorOrder,
} from "../../../shared/beachBumzOrders";
import "./BeachBumzDashboard.css";
import VendorMasterCalendar from "./VendorMasterCalendar";
import TeamWorkspace from "./TeamWorkspace";
import type { TeamApi } from "./teamRequest";
import type { ScheduleEntry } from "../../../shared/beachBumzSchedule";
const VendorBunkViewer = lazy(() => import("./VendorBunkViewer"));

export type VendorRequest = (
  body?: Record<string, unknown>,
) => Promise<{ notificationEmail?: string; confirmationSent?: boolean; canRelease?: boolean; orders?: VendorOrder[]; order?: VendorOrder; calendar?: ScheduleEntry[]; calendarVersion?: string }>;
type Props = {
  request: VendorRequest;
  onSignOut: () => void;
  ownerName: string;
  catalog: ReactNode;
  teamRequest?: TeamApi;
  previewOnly?: boolean;
};
const tabs = [
  ["Overview", Home],
  ["Orders", ClipboardList],
  ["Production", Factory],
  ["Fabrication Manager", Factory],
  ["Shop TV Live", Monitor],
  ["Install Manager", Truck],
  ["Installations", Truck],
  ["Service", Wrench],
  ["Payments", CreditCard],
  ["Catalog", BookOpen],
] as const;
const businessTools = [
  {name:"My clients", description:"Keep contacts, notes and follow-ups for customers beyond Bliss Bedrooms.", trial:true},
  {name:"My jobs", description:"Manage projects for your other clients alongside your Bliss work.", trial:true},
  {name:"Business calendar", description:"Plan your workload across clients and coordinate your schedule.", trial:true},
  {name:"Quotes & invoices", description:"Prepare estimates and invoices for your own customers.", trial:false},
  {name:"Business reporting", description:"Review performance across your business.", trial:false},
  {name:"Realtimax", description:"Explore the future platform connecting businesses, clients and partners.", trial:false},
];
const stages = ["Review", "Fabricate", "Paint", "Install", "Complete"];
const money = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
const labels = {
  review: "Awaiting acceptance",
  accepted: "Dates proposed",
  fabricating: "In fabrication",
  painting: "In finishing",
  installing: "Installing",
  complete: "Complete",
};
const nextLabels = {
  accepted: "Start fabrication",
  fabricating: "Move to paint",
  painting: "Start installation",
  installing: "Mark complete",
};
function LadderIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path d="M6 2v20M18 2v20M6 6h12M6 12h12M6 18h12" />
    </svg>
  );
}
export default function BeachBumzDashboard({
  request,
  onSignOut,
  ownerName,
  catalog,
  teamRequest,
  previewOnly = false,
}: Props) {
  const [calendar, setCalendar] = useState<ScheduleEntry[]>([]);
  const [demoCalendar, setDemoCalendar] = useState<ScheduleEntry[]>([]);
  const [calendarVersion, setCalendarVersion] = useState("");
  const [canRelease, setCanRelease] = useState(false);
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [selected, setSelected] = useState("");
  const [demo, setDemo] = useState(false);
  const [view3d, setView3d] = useState(true);
  const [demoOrder, setDemoOrder] = useState<VendorOrder>({
    ...DEMO_VENDOR_ORDER,
  });
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [notificationEmail,setNotificationEmail] = useState("");
  const [savedNotificationEmail,setSavedNotificationEmail] = useState("");
  const [emailSaving,setEmailSaving] = useState(false);
  const [emailMessage,setEmailMessage] = useState("");
  async function saveNotificationEmail(event: React.FormEvent) {
    event.preventDefault();
    if(demo || emailSaving)return;
    setEmailSaving(true);setEmailMessage("");
    try {
      const result=await request({action:"notification-email-save",email:notificationEmail.trim()});
      const saved=result.notificationEmail || notificationEmail.trim();
      setNotificationEmail(saved);setSavedNotificationEmail(saved);setEmailMessage(result.confirmationSent?"Email saved. Check your inbox for a dashboard link.":"Email saved, but the confirmation email could not be sent.");
    } catch(error) {setEmailMessage(error instanceof Error?error.message:"Email could not be saved. Please try again.");}
    finally {setEmailSaving(false);}
  }
  const [showAttention, setShowAttention] = useState(false);
  const visibleOrders = demo ? [demoOrder] : orders;
  function toggleDemo() {
    setDemo(!demo);
    setSelected(!demo ? "demo" : orders[0]?.id || "");
    setForm(null);
    setNotice("");
    setSearch("");
    setStageFilter("");
  }
  function exportPayments() {
    const cell = (value: string | number) =>
      '"' +
      String(value)
        .replace(/^[=+@-]/, "'$&")
        .replaceAll('"', '""') +
      '"';
    const rows = [
      ["Order", "Configuration", "Vendor amount USD", "Payment status"],
      ...filtered.map((o) => [o.number, o.title, o.wholesalePending ? "Pending wholesale" : o.amountUsd, o.paymentStatus]),
    ];
    const url = URL.createObjectURL(
      new Blob([rows.map((row) => row.map(cell).join(",")).join("\r\n")], {
        type: "text/csv;charset=utf-8",
      }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = demo
      ? "example-vendor-payments.csv"
      : "beach-bumz-payments.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
  const [tab, setTab] = useState("Orders");
  const [tvJobId,setTvJobId]=useState("");
  const [gcodeFileId,setGcodeFileId]=useState("");
  async function pushToTv(){
    if(!order)return;
    if(demo){setTab("Shop TV Live");return;}
    if(!teamRequest){setError("Team workspace connection unavailable.");return;}
    setSaving(true);setError("");
    try{const data=await teamRequest();const job=data.jobs?.find(j=>j.id===order.id);if(!job)throw new Error("Release this order to Beach Bumz first.");await teamRequest({action:"tv-publish",id:job.id,version:job.version});setTvJobId(job.id);setTab("Shop TV Live");}
    catch(e){setError((e as Error).message);}finally{setSaving(false);}
  }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState<"accept" | "issue" | null>(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [issue, setIssue] = useState("");
  const [saving, setSaving] = useState(false);
  async function refresh() {
    setLoading(true);
    setError("");
    try {
      const data = await request();
      setNotificationEmail(data.notificationEmail || "");
      setSavedNotificationEmail(data.notificationEmail || "");
      setCanRelease(data.canRelease === true);
      setOrders(data.orders || []);
      setCalendar(data.calendar || []);
      setCalendarVersion(data.calendarVersion || "");
      setSelected((id) =>
        (data.orders || []).some((o) => o.id === id)
          ? id
          : data.orders?.[0]?.id || "",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load orders.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, [request]);
  const order = demo ? demoOrder : orders.find((o) => o.id === selected);
  function choose(id: string) {
    setSelected(id);
    setTab("Orders");
    setForm(null);
    setNotice("");
  }
  async function updateCalendar(action: string, entry?: ScheduleEntry, entryId?: string) {
    if (demo) {
      setDemoCalendar(rows => action === "calendar-delete" ? rows.filter(r => r.id !== entryId) : [...rows.filter(r => r.id !== entry!.id), entry!]);
      return;
    }
    const result = await request({action, entry, entryId, calendarVersion});
    if (!result.calendar || typeof result.calendarVersion !== "string") throw new Error("Calendar save was not confirmed. Refresh and retry.");
    setCalendar(result.calendar); setCalendarVersion(result.calendarVersion);
  }
  const masterCalendar = <VendorMasterCalendar key={demo ? "demo-calendar" : "live-calendar"} orders={visibleOrders} entries={demo ? demoCalendar : calendar} demo={demo} selectedId={order?.id} draftStart={form === "accept" ? start : undefined} draftEnd={form === "accept" ? end : undefined} onSave={entry => updateCalendar("calendar-save", entry)} onDelete={id => updateCalendar("calendar-delete", undefined, id)} />;
  async function save(action: string) {
    if (!order) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      if (demo) {
        const patch = action === "notify" ? {notifiedStage: order.stage, notifiedAt: new Date().toISOString()} : vendorActionPatch(
          order,
          { action, start, end, issue },
          new Date().toISOString().slice(0, 10),
        );
        setDemoOrder({ ...order, ...patch } as VendorOrder);
        setForm(null);
        setNotice(action === "notify" ? "Example notification recorded locally. No email was sent." : "Preview updated locally. No live order was changed.");
        return;
      }
      const result = await request({
        id: order.id,
        version: order.version,
        action,
        gcodeFileId,
        start,
        end,
        issue,
      });
      if (!result.order)
        throw new Error(
          "The update could not be confirmed. Refresh and try again.",
        );
      setOrders((rows) =>
        rows.map((o) => (o.id === result.order!.id ? result.order! : o)),
      );
      setForm(null);
      setNotice(
        action === "notify" ? "Bliss has been notified by email and can update the customer." : action === "accept"
          ? "Saved. Your installation window is proposed; Bliss will confirm dates."
          : "Order updated.",
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Unable to save. Your changes were not confirmed.",
      );
    } finally {
      setSaving(false);
    }
  }
  const filtered = visibleOrders
    .filter(
      (o) =>
        (!stageFilter || o.stage === stageFilter) &&
        `${o.number} ${o.title} ${o.zip}`
          .toLowerCase()
          .includes(search.toLowerCase()),
    )
    .filter((o) =>
      tab === "Production"
        ? ["accepted", "fabricating", "painting"].includes(o.stage)
        : tab === "Installations"
          ? o.stage !== "complete"
          : tab === "Service"
            ? !!o.issue
            : true,
    );
  const businessTool = businessTools.find(tool=>tool.name===tab);
  const openBusinessTool = (name:string) => {setDemo(false);setTab(name);setError("");setForm(null);setNotice("");setShowAttention(false);};
  const step = order
    ? Math.max(
        0,
        ["review", "fabricating", "painting", "installing", "complete"].indexOf(
          order.stage,
        ),
      )
    : 0;
  return (
    <div className="bbv-shell">
      <aside className="bbv-sidebar">
        <div className="bbv-sidebar-inner">
          <div className="bbv-brand">
            <div className="bbv-logo">
              <img
                src="/bliss-basic/beach-bumz/logo.png"
                alt="Beach Bumz penguin and surfboard"
              />
            </div>
            <strong>BEACH BUMZ</strong>
            <span>Partner workspace</span>
          </div>
          <div className="bbv-profile">
            <span className="bbv-avatar">{ownerName.charAt(0)}</span>
            <span>
              {ownerName}
              <small>Vendor admin</small>
            </span>
            <button aria-label="Sign out" onClick={onSignOut}>
              <LogOut size={19} />
            </button>
          </div>
          <nav aria-label="Partner workspace">
            <span className="bbv-nav-section-label">Bliss fulfillment</span>
            {tabs.map(([name, Icon]) => (
              <button
                key={name}
                aria-current={tab === name ? "page" : undefined}
                onClick={() => {
                  setTab(name);
                  setError("");
                  setSearch("");
                  setStageFilter("");
                  setNotice("");
                  setForm(null);
                }}
              >
                <Icon size={21} />
                {name}
              </button>
            ))}
          </nav>
          <details className="bbv-business-menu" open>
            <summary>My business <span>Free tools</span></summary>
            <nav aria-label="Business tools">{businessTools.filter(tool=>tool.trial).map(tool=><button key={tool.name} aria-current={tab===tool.name?"page":undefined} onClick={()=>openBusinessTool(tool.name)}><span>{tool.name}<small>Free</small></span><ChevronRight size={16}/></button>)}</nav>
          </details>
          <details className="bbv-business-menu">
            <summary>Coming soon</summary>
            <nav aria-label="Upcoming tools">{businessTools.filter(tool=>!tool.trial).map(tool=><button key={tool.name} aria-current={tab===tool.name?"page":undefined} onClick={()=>openBusinessTool(tool.name)}><span>{tool.name}<small>Coming soon</small></span><ChevronRight size={16}/></button>)}</nav>
          </details>
          <p className="bbv-tools-note">Business tools are provided by Bliss Bedrooms. Bliss is one of your clients.</p>

        </div>
      </aside>
      <main className="bbv-main">
        <header className="bbv-heading">
          <div>
            <div className="bbv-title-row">
              <h1>
                {tab === "Orders" && order
                  ? `Order ${order.number}`
                  : tab === "Orders"
                    ? "Your orders"
                    : tab}
              </h1>
              {demo && !businessTool && <span className="bbv-demo">DEMO ORDER</span>}
            </div>
            <p>{businessTool ? "Beach Bumz · Business tools" : "Bliss Bedrooms · Client fulfillment"}</p>
          </div>
          <div className="bbv-heading-actions">
            <button
              className="bbv-icon-button"
              aria-label="Orders needing attention"
              aria-expanded={showAttention}
              onClick={() => setShowAttention(!showAttention)}
            >
              <Bell size={20} />
            </button>
            <button
              className="bbv-icon-button"
              onClick={() => void refresh()}
              disabled={loading}
              aria-label="Refresh orders"
            >
              <RefreshCw size={20} />
            </button>
            {order && tab === "Orders" && (
              <span className="bbv-status">
                <Clock size={18} />
                {labels[order.stage]}
              </span>
            )}
          </div>
        </header>
        {error && (
          <div role="alert" className="bbv-alert">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => void refresh()}>Retry</button>
          </div>
        )}
        {notice && (
          <p role="status" className="bbv-notice">
            {notice}
          </p>
        )}
        <div className="bbv-preview-controls" hidden={!!businessTool}>
          <button className="bbv-text-button" onClick={toggleDemo}>
            {demo ? "Return to live orders" : "View example order"}
          </button>
          {demo && (
            <button
              className="bbv-text-button"
              onClick={() => {
                setDemoOrder({ ...DEMO_VENDOR_ORDER });
                setNotice("Example reset.");
                setForm(null);
              }}
            >
              Reset example
            </button>
          )}
        </div>
        {demo && !businessTool && (
          <p className="bbv-demo-note">
            Interactive example — changes stay in this browser session. No
            orders, bookings or payments are sent.
          </p>
        )}
        {showAttention && (
          <section className="bbv-card bbv-attention">
            <h2>Needs attention</h2>
            <form className="bbv-notification-email" onSubmit={saveNotificationEmail}>
              <label htmlFor="bbv-notification-email">Notification email</label>
              <div><input id="bbv-notification-email" type="email" autoComplete="email" required maxLength={254} placeholder="you@example.com" value={notificationEmail} disabled={demo || emailSaving || loading} onChange={event=>{setNotificationEmail(event.target.value);setEmailMessage("");}}/><button className="bbv-primary" type="submit" disabled={demo || loading || emailSaving || !notificationEmail.trim() || notificationEmail.trim()===savedNotificationEmail}>{emailSaving?"Saving…":"Save"}</button></div>
              <small>{demo?"Switch to live orders to save an email.":"Save or change your preferred address here. Automatic email alerts are not enabled yet."}</small>
              {emailMessage&&<p role="status">{emailMessage}</p>}
            </form>
            {visibleOrders
              .filter(
                (o) =>
                  o.stage === "review" ||
                  o.issue ||
                  (o.stage === "accepted" && !o.productionReleased),
              )
              .map((o) => (
                <button
                  key={o.id}
                  className="bbv-order-row"
                  onClick={() => {
                    choose(o.id);
                    setShowAttention(false);
                  }}
                >
                  <span>
                    {o.number} ·{" "}
                    {o.issue ||
                      (o.stage === "review"
                        ? "Review scope and propose dates"
                        : "Awaiting Bliss production release")}
                  </span>
                  <ChevronRight />
                </button>
              ))}
            {!visibleOrders.some(
              (o) =>
                o.stage === "review" ||
                o.issue ||
                (o.stage === "accepted" && !o.productionReleased),
            ) && <p>No orders need your attention.</p>}
          </section>
        )}
        {(businessTool?.trial || (tab==="Production"&&!demo)) ? <VendorBusinessWorkspace key={tab} view={tab} previewOnly={previewOnly} onOpenBliss={id=>{setDemo(false);choose(id);setTab("Orders");}}/> : businessTool ? <section className="bbv-card bbv-business-intro">
          <span className="bbv-tool-status">{businessTool.trial ? "3-month trial · not yet enabled" : "Coming soon"}</span>
          <h2>{businessTool.name==="My clients" ? "Your clients, in one place." : businessTool.name}</h2>
          <p>{businessTool.description}</p>
          {businessTool.trial ? <p>Once enabled by Bliss, you can choose to activate three months of access. Opening this tab does not start a trial. Your trial dates will appear here when activation is available.</p> : <p>This feature is in development and is not available yet.</p>}
          <p>Your Bliss order fulfillment stays available separately from these optional tools.</p>
          <button className="bbv-text-button" onClick={()=>setTab("Orders")}>Return to Bliss orders →</button>
        </section> : ["Fabrication Manager", "Install Manager", "Shop TV Live"].includes(tab) ? (demo && tab !== "Shop TV Live" ? <section className="bbv-card"><h2>Team workspaces</h2><p>Return to live orders to assign jobs and manage crew access.</p><button onClick={toggleDemo}>Open live team workspace</button></section> : teamRequest ? <TeamWorkspace key={tab} previewOnly={previewOnly} hideRoleSwitcher={previewOnly} api={teamRequest} initialDepartment={tab === "Install Manager" ? "installation" : "fabrication"} initialTv={tab === "Shop TV Live"} initialDemo={demo && tab === "Shop TV Live"} initialJobId={tvJobId} onExitTv={()=>setTab("Orders")} /> : <p role="status">Team workspace connection unavailable.</p>) : tab === "Catalog" ? (
          <section className="bbv-catalog">
            {demo ? (
              <>
                <h2>Catalog editor</h2>
                <p>
                  The catalog manages real published products. Return to live
                  orders to edit it.
                </p>
              </>
            ) : (
              catalog
            )}
          </section>
        ) : loading ? (
          <section className="bbv-card bbv-empty" aria-live="polite">
            Loading your released purchase orders…
          </section>
        ) : tab !== "Orders" ? (
          <>
            {tab === "Overview" && (
              <div className="bbv-metrics">
                {[
                  ["Released orders", visibleOrders.length],
                  [
                    "In production",
                    visibleOrders.filter((o) =>
                      ["fabricating", "painting"].includes(o.stage),
                    ).length,
                  ],
                  ["Scope issues", visibleOrders.filter((o) => o.issue).length],
                ].map(([label, value]) => (
                  <div className="bbv-card" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            )}
            <section className="bbv-card bbv-list">
              <p className="bbv-view-description">
                {
                  {
                    Overview:
                      "Your active purchase orders, production workload and unresolved issues.",
                    Production:
                      "Open an order to update fabrication and paint progress. Bliss must release production first.",
                    Installations:
                      "Review proposed windows and confirmed dates. Open an order to propose or update dates.",
                    Service:
                      "Open an order to review or update its reported issue. Bliss resolves scope holds.",
                    Payments:
                      "Vendor compensation and payment status supplied by Bliss. This page does not transfer funds.",
                  }[tab]
                }
              </p>
              <div className="bbv-list-tools">
                <label>
                  Find an order
                  <input
                    type="search"
                    placeholder="Order, configuration or ZIP"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
                <label>
                  Stage
                  <select
                    value={stageFilter}
                    onChange={(e) => setStageFilter(e.target.value)}
                  >
                    <option value="">All stages</option>
                    {Object.entries(labels).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {tab === "Installations" && masterCalendar}
              {tab === "Payments" && (
                <div className="bbv-payment-summary">
                  <span>
                    Listed purchase order total
                    <strong>
                      {money(filtered.reduce((sum, o) => sum + o.amountUsd, 0))}
                    </strong>
                  </span>
                  <button
                    className="bbv-secondary"
                    onClick={exportPayments}
                    disabled={!filtered.length}
                  >
                    <Download size={18} />
                    Export CSV
                  </button>
                </div>
              )}
              {tab === "Production" && (
                <div className="bbv-production-counts">
                  {["accepted", "fabricating", "painting"].map((stage) => (
                    <button
                      className="bbv-secondary"
                      key={stage}
                      onClick={() =>
                        setStageFilter(stageFilter === stage ? "" : stage)
                      }
                      aria-pressed={stageFilter === stage}
                    >
                      {labels[stage]} ·{" "}
                      {visibleOrders.filter((o) => o.stage === stage).length}
                    </button>
                  ))}
                </div>
              )}
              <h2>
                {tab === "Payments"
                  ? "Vendor purchase orders"
                  : `${tab === "Overview" ? "Recent" : tab} orders`}
              </h2>
              {filtered.length ? (
                filtered.map((o) => (
                  <button
                    className="bbv-order-row"
                    key={o.id}
                    onClick={() => choose(o.id)}
                  >
                    <span>
                      <strong>
                        {o.number} · {o.title}
                      </strong>
                      <small>
                        {tab === "Service"
                          ? o.issue
                          : tab === "Installations"
                            ? `${o.installationConfirmed ? "Confirmed: " + o.installationConfirmed : o.proposedStart ? "Proposed: " + o.proposedStart + " – " + o.proposedEnd : "Dates needed"} · ZIP ${o.zip || "pending"}`
                            : labels[o.stage]}
                      </small>
                    </span>
                    <span>
                      {tab === "Payments" ? (
                        <>
                          {o.wholesalePending ? "Pending wholesale" : money(o.amountUsd)}
                          <small>{o.paymentStatus}</small>
                        </>
                      ) : (
                        <ChevronRight />
                      )}
                    </span>
                  </button>
                ))
              ) : (
                <p>
                  No{" "}
                  {tab === "Service"
                    ? "reported scope issues"
                    : "released orders in this view"}
                  .
                </p>
              )}
              {tab === "Payments" && (
                <p className="bbv-muted">
                  Purchase order amounts are vendor compensation. Payment status
                  is maintained by Bliss. If payment is not scheduled, Bliss must confirm the schedule; this view does not provide a payment date.
                </p>
              )}
            </section>
          </>
        ) : (
          <>
            <div className="bbv-order-picker">
              {visibleOrders.length > 0 && (
                <label>
                  Purchase order{" "}
                  <select
                    value={demo ? demoOrder.id : selected}
                    onChange={(e) => choose(e.target.value)}
                  >
                    <option value="" disabled>
                      Select an order
                    </option>
                    {visibleOrders.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.number} · {o.title}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            {!order ? (
              <section className="bbv-card bbv-empty">
                <ClipboardList size={40} />
                <h2>
                  {error ? "Orders unavailable" : "Ready for your next order"}
                </h2>
                <p>
                  {error
                    ? "Retry to load your orders."
                    : "Purchase orders will appear here when Bliss releases them to Beach Bumz."}
                </p>
                <button className="bbv-primary" onClick={toggleDemo}>
                  Preview the example order
                </button>
              </section>
            ) : (
              <>
                <div className="bbv-workflow-wrap"><ol className="bbv-workflow" aria-label="Fulfillment progress">
                  {stages.map((label, i) => (
                    <li
                      key={label}
                      className={i <= step ? "is-active" : ""}
                      aria-current={i === step ? "step" : undefined}
                    >
                      <span>{i < step ? <Check size={16} /> : i + 1}</span>
                      {label}
                    </li>
                  ))}
                </ol>
                {order.stage !== "review" && <div className="bbv-notify"><button type="button" className="bbv-primary" disabled={saving || order.notifiedStage === order.stage} onClick={() => void save("notify")}><Bell size={17}/>{order.notifiedStage === order.stage ? "Client notified" : "Notify Client"}</button><small>{order.notifiedStage === order.stage && order.notifiedAt ? `${demo ? "Example recorded" : "Sent"} ${new Date(order.notifiedAt).toLocaleString()}` : "Notify Bliss about this phase. Bliss contacts the customer."}</small></div>}
                </div>
                <div className="bbv-order-grid">
                  <section className="bbv-card bbv-product">
                    <h2>{order.title}</h2>
                    <p>
                      {order.layout} · {order.access} · {order.quantity}{" "}
                      complete {order.quantity === 1 ? "set" : "sets"}
                    </p>
                    {demo && <div className="bbv-view-toggle" aria-label="Bunk preview mode"><button type="button" aria-pressed={!view3d} onClick={() => setView3d(false)}>Photo</button><button type="button" aria-pressed={view3d} onClick={() => setView3d(true)}>Rotate in 3D</button></div>}
                    {demo && view3d ? <Suspense fallback={<p role="status">Loading 3D preview…</p>}><VendorBunkViewer /></Suspense> : <div className="bbv-product-image">
                      {order.imageUrl ? (
                        <img
                          src={order.imageUrl}
                          alt={`${order.title}, ${order.access}`}
                        />
                      ) : (
                        <div className="bbv-no-image">
                          <BedDouble size={54} />
                          <span>Approved construction render pending</span>
                        </div>
                      )}
                    </div>
                    }
                    <small className="bbv-muted">
                      Use approved production drawings for dimensions and
                      joinery.
                    </small>
                    <dl className="bbv-specs">
                      {[
                        [BedDouble, "Upper", order.upper],
                        [BedDouble, "Lower", order.lower],
                        [PaintRoller, "Finish", order.finish],
                        [LadderIcon, "Access", order.access],
                        [
                          Layers,
                          "Quantity",
                          `${order.quantity} ${order.quantity === 1 ? "set" : "sets"}`,
                        ],
                        [MapPin, "Jobsite ZIP", order.zip || "Pending"],
                      ].map(([Icon, label, value]: any) => (
                        <div key={label}>
                          <Icon />
                          <span>
                            <dt>{label}</dt>
                            <dd>{value || "Pending"}</dd>
                          </span>
                        </div>
                      ))}
                    </dl>
                    {form === "accept" && masterCalendar}
                  </section>
                  <div className="bbv-right">
                    {canRelease && !demo && <VendorReleaseControls key={order.id+order.version} order={order} onSave={async fields=>{await request({id:order.id,version:order.version,action:'bliss-release',...fields});await refresh();}}/>}
                    {!!order.finishSpecifications?.length&&<section className="bbv-card"><h2>Paint &amp; Finish Specifications</h2>{order.finishSpecifications.map((spec,i)=><div key={i} style={{borderBottom:'1px solid #ddd5c5',padding:'12px 0'}}><h3>{spec.room} · Bunk {spec.bunk}</h3><p>{spec.finish}</p>{spec.finish==='Custom Color'&&<p><strong>Sherwin-Williams: {spec.paintCode||'Awaiting customer selection'}</strong></p>}</div>)}<p>Confirm the selected color with Bliss before painting.</p></section>}
                    {!!order.scope?.length && <section className="bbv-card"><h2>Purchased scope</h2><ul>{order.scope.map((line,i)=><li key={i}>{line}</li>)}</ul><p>Turnkey fulfillment: fabrication, finishing, packing, delivery and installation.</p></section>}
                    <section className="bbv-card bbv-amount">
                      <h2>Your vendor amount</h2>
                      <strong className="bbv-price">
                        {order.wholesalePending ? "Awaiting wholesale confirmation" : money(order.amountUsd)}
                      </strong>
                      <p>
                        {order.demo
                          ? "Proposed purchase order"
                          : "Released purchase order"}
                      </p>
                      <dl>
                        {order.lineItems.map((item, i) => (
                          <div key={i}>
                            <dt>{item.label}</dt>
                            <dd>{money(item.amountUsd)}</dd>
                          </div>
                        ))}
                        <div className="bbv-included">
                          <dt>Paint & installation</dt>
                          <dd>Included</dd>
                        </div>
                      </dl>
                      {order.demo && (
                        <p className="bbv-warning">
                          <AlertCircle size={18} />
                          Offset layout and ladder inclusion: confirm
                        </p>
                      )}
                      <div className="bbv-payment">
                        <CreditCard size={18} />
                        <span>Payment status</span>
                        <span>{order.paymentStatus}</span>
                      </div>
                      {order.paymentStatus === "Not scheduled" && <p className="bbv-muted">Next step · Bliss: confirm the payment schedule. No payment date has been provided here.</p>}
                    </section>
                    <section className="bbv-card bbv-actions">
                      <h2>Next action</h2>
                      {order.issue && <p className="bbv-warning">Blocked by a reported issue. Josh and Bliss: resolve the scope issue before advancing this order.</p>}
                      {!order.productionReleased && order.stage !== "review" && order.stage !== "complete" && <p className="bbv-warning">Waiting on Bliss: production release is required before work can advance.</p>}
                      {order.stage === "painting" && !order.installationConfirmed && <p className="bbv-warning">Waiting on Bliss: confirm the installation window before moving to installation.</p>}
                      <button className="bbv-primary" disabled={saving} onClick={()=>void pushToTv()}><Monitor size={18}/> {demo?"Preview BB-1042 on Shop TV":"Push to Shop TV"}</button>
                      <p>
                        {order.stage === "review"
                          ? "Review the scope and propose your installation window."
                          : order.stage === "complete"
                            ? "Installation is complete. Report any service issue below."
                            : "Keep Bliss updated as your order moves through fulfillment."}
                      </p>
                      {
                        <>
                          {order.wholesalePending && <p className="bbv-warning">Bliss is confirming your wholesale purchase order. Review the scope now; acceptance opens when the amount is confirmed.</p>}
                          {["review", "accepted"].includes(order.stage) && !order.wholesalePending && (
                            <button
                              className="bbv-primary"
                              onClick={() => {
                                setStart(order.proposedStart || "");
                                setEnd(order.proposedEnd || "");
                                setForm("accept");
                              }}
                            >
                              <Check size={20} />
                              {order.stage === "review"
                                ? "Accept scope & propose dates"
                                : "Update proposed dates"}
                            </button>
                          )}
                          {nextLabels[
                            order.stage as keyof typeof nextLabels
                          ] && (
                            <button
                              className="bbv-primary"
                              disabled={
                                saving ||
                                !order.productionReleased ||
                                !!order.issue ||
                                (order.stage === "painting" &&
                                  !order.installationConfirmed)
                              }
                              onClick={() => void save("advance")}
                            >
                              {
                                nextLabels[
                                  order.stage as keyof typeof nextLabels
                                ]
                              }
                            </button>
                          )}
                          {order.stage === "accepted" && <OrderGcodeUpload key={order.id+(demo?"demo":"live")} api={teamRequest} orderId={order.id} demo={demo} onSelect={setGcodeFileId} onUploaded={()=>void refresh()}/>}
                          <button
                            className="bbv-secondary"
                            onClick={() => {
                              setIssue(order.issue || "");
                              setForm("issue");
                            }}
                          >
                            <AlertCircle size={20} />
                            {order.stage === "complete"
                              ? "Report a service issue"
                              : "Flag a scope issue"}
                          </button>
                        </>
                      }
                      <small>Scope changes require Bliss approval.</small>
                      {order.stage !== "review" &&
                        order.stage !== "complete" &&
                        !order.productionReleased && (
                          <p className="bbv-warning">
                            Awaiting approved dimensions and production release
                            from Bliss.
                          </p>
                        )}
                      {demo && order.stage !== "review" && (
                        <div className="bbv-demo-controls">
                          <strong>
                            Preview controls · simulate Bliss approval
                          </strong>
                          <button
                            className="bbv-secondary"
                            onClick={() =>
                              setDemoOrder({
                                ...demoOrder,
                                productionReleased: true,
                                installationConfirmed: demoOrder.proposedStart,
                                issue: null,
                              })
                            }
                          >
                            Simulate release & date confirmation
                          </button>
                          <small>
                            Only affects the example. Live approval remains with
                            Bliss.
                          </small>
                        </div>
                      )}
                      {order.issue && (
                        <p className="bbv-warning">{order.issue}</p>
                      )}
                      {form && (
                        <form
                          className="bbv-action-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            void save(form);
                          }}
                        >
                          <div className="bbv-title-row">
                            <h3>
                              {form === "accept"
                                ? "Propose installation dates"
                                : "Describe the issue"}
                            </h3>
                            <button
                              type="button"
                              className="bbv-icon-button"
                              aria-label="Close form"
                              onClick={() => setForm(null)}
                            >
                              <X size={18} />
                            </button>
                          </div>
                          {form === "accept" ? (
                            <>
                              <label>
                                Earliest date
                                <input
                                  required
                                  type="date"
                                  min={new Date().toISOString().slice(0, 10)}
                                  value={start}
                                  onChange={(e) => setStart(e.target.value)}
                                />
                              </label>
                              <label>
                                Latest date
                                <input
                                  required
                                  type="date"
                                  min={
                                    start ||
                                    new Date().toISOString().slice(0, 10)
                                  }
                                  value={end}
                                  onChange={(e) => setEnd(e.target.value)}
                                />
                              </label>
                              <p>
                                Proposed dates are subject to Bliss
                                confirmation.
                              </p>
                            </>
                          ) : (
                            <label>
                              Details
                              <textarea
                                required
                                maxLength={2000}
                                rows={4}
                                value={issue}
                                onChange={(e) => setIssue(e.target.value)}
                              />
                            </label>
                          )}
                          <button className="bbv-primary" disabled={saving}>
                            {saving ? "Saving…" : "Save update"}
                          </button>
                        </form>
                      )}
                    </section>
                  </div>
                </div>
                <section className="bbv-card bbv-operations">
                  <div>
                    <FileCheck />
                    <span>
                      <strong>Production release</strong>
                      <small>
                        {order.productionReleased
                          ? "Released by Bliss"
                          : "Pending approved dimensions"}
                      </small>
                    </span>
                  </div>
                  <div>
                    <CalendarDays />
                    <span>
                      <strong>Installation</strong>
                      <small>
                        {order.installationConfirmed
                          ? `Confirmed: ${order.installationConfirmed}`
                          : order.proposedStart
                            ? `Proposed: ${order.proposedStart} – ${order.proposedEnd}`
                            : "Dates not confirmed"}
                      </small>
                    </span>
                  </div>
                  <div>
                    <Wrench />
                    <span>
                      <strong>Service ownership</strong>
                      <small>Beach Bumz handles callbacks.</small>
                    </span>
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

